import { useEffect, useMemo, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useSessionData } from '@confluence/session-data';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations';
import {
	ActiveCommentsQuery,
	buildConsistentActiveCommentsQueryPreloadVariables,
	buildConsistentActiveCommentsQueryVariables,
	ACTIVE_COMMENTS_QUERY_PRELOAD_LOADING_FLAG,
	type ActiveCommentsQueryType,
	type CommentsType,
	type ActiveCommentsQueryVariables,
} from '@confluence/comments-panel-queries';

import { FooterCommentsLoadingStateType, useCommentsData } from '../useCommentsData';
import {
	updateCommentsDataState,
	updateFooterCommentsLoadingState,
} from '../helper/commentsDataHelper';

export type useActiveCommentsQueryVariables = {
	pageId: string;
	type?: CommentsType;
	skip?: boolean;
	onCompletedFn?: () => void;
	returnNativeLoading?: boolean;
	useStableCommentsDataUpdates?: boolean;
	fetchPolicy?: 'cache-only' | 'cache-and-network' | 'network-only';
	isPreload?: boolean;
};

export const useActiveCommentsQuery = ({
	pageId,
	skip = false,
	type,
	onCompletedFn,
	returnNativeLoading = false, // Doing this in the useEffect has slightly different behavior than the native graphQL query, this lets us choose which behavior we want
	useStableCommentsDataUpdates = false,
	fetchPolicy,
	isPreload = false,
}: useActiveCommentsQueryVariables) => {
	// The query loading was not sticking around long enough or triggering at the right times
	// so I created a loading state that will set after the query is successful/fails
	const [loading, setLoading] = useState(true);
	const [
		{ commentsDataMap, cacheUpdateFailed },
		{ addNewCommentThreads, setCommentsDataMap, setFooterCommentsLoadingState },
	] = useCommentsData();

	const [{ readCommentsListState }] = useUnreadComments();

	const [{ annotations }] = useEditorAnnotations();
	const pageMode = useGetPageMode();
	const { userId: currentUserId } = useSessionData();
	const { contentStatus } = useContentStatus();

	const annotationsInEditorDoc = useMemo(() => {
		return new Set(annotations);
	}, [annotations]);

	// Determine if the query should be skipped
	const skipQuery = !pageId || skip;

	const hasAlreadyFetchedInitialData =
		Object.keys(commentsDataMap.inline).length > 0 ||
		Object.keys(commentsDataMap.general).length > 0;

	// Check if the page is archived
	const isArchived = contentStatus === CONTENT_STATUS_ARCHIVED;

	// Using preload variables isn't 100% necessary since we write to the full cache,
	// but should be more stable in the event the full cache update fails
	const queryVariables = isPreload
		? buildConsistentActiveCommentsQueryPreloadVariables(pageId, isArchived)
		: buildConsistentActiveCommentsQueryVariables({ pageId, type, isArchived });

	const cacheUpdateFailedFetchPolicy =
		cacheUpdateFailed && fg('confluence_comment_donot_use_cache') ? 'network-only' : undefined;

	const defaultFetchPolicy = skipQuery || isPreload ? 'cache-only' : 'cache-and-network'; // TODO: Cache and network probably not perfect, used to ensure consistency on the panel

	// Execute the query
	const {
		data,
		loading: queryLoading,
		error,
		refetch,
	} = useQuery<ActiveCommentsQueryType, ActiveCommentsQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		ActiveCommentsQuery,
		{
			variables: queryVariables,
			// Skip is not respected after the initial call: https://github.com/apollographql/apollo-client/issues/6190#issuecomment-649933628
			fetchPolicy: fetchPolicy || cacheUpdateFailedFetchPolicy || defaultFetchPolicy,
			skip: skipQuery,
			onCompleted: (result) => {
				// onCompleted doesn't fire as expected after multiple cache reads
				if (!useStableCommentsDataUpdates && result) {
					updateCommentsDataState({
						data: result,
						readCommentsListState,
						updateData: hasAlreadyFetchedInitialData ? addNewCommentThreads : setCommentsDataMap,
						annotationsInEditorDoc,
						pageMode,
						currentUserId: currentUserId ?? '',
						isOpen: true,
					});

					if (!skipQuery) {
						// Don't update loading state if the query is skipped
						// onCompleted will still get called in some circumstances with skip = true
						updateFooterCommentsLoadingState(data, setFooterCommentsLoadingState);
					}
				}
				setLoading(false);
				if (result) {
					onCompletedFn && onCompletedFn();
				}
			},
			onError: fg('confluence_frontend_handle_reactions_errors')
				? undefined
				: (err) => {
						// If the error is related to reactionsSummary returning null, just mark it handled
						// or if the error is related to cc-pages not being reachable, just mark it handled
						if (err.message.search(/reactions/i) !== -1 || err.message.search(/cc-pages/i) !== -1) {
							markErrorAsHandled(err);
						}

						setLoading(false);
					},
			errorPolicy: fg('confluence_frontend_handle_reactions_errors') ? 'all' : 'none',
		},
	);

	useEffect(() => {
		if (error && fg('confluence_frontend_handle_reactions_errors')) {
			// If the error is related to reactionsSummary returning null, just mark it handled
			// or if the error is related to cc-pages not being reachable, just mark it handled
			if (error.message.search(/reactions/i) !== -1 || error.message.search(/cc-pages/i) !== -1) {
				markErrorAsHandled(error);
			}

			setLoading(false);
		}
	}, [error]);

	useEffect(() => {
		if (queryLoading) {
			setLoading(true);
		}
	}, [queryLoading]);

	let returnLoading = queryLoading;

	// If we're not returning the native loading out of apollo, update returnLoading to our tracked version
	if (!returnNativeLoading) {
		returnLoading = !skipQuery ? loading : false;
	}

	// We set totalCount to ACTIVE_COMMENTS_QUERY_PRELOAD_LOADING_FLAG when preload is loading
	// We should respect this regardless of context, since the query really is loading behind the scenes
	const isPreloadLoading =
		data?.comments?.totalCount === ACTIVE_COMMENTS_QUERY_PRELOAD_LOADING_FLAG;

	returnLoading = isPreloadLoading || returnLoading;

	useEffect(() => {
		// onCompleted gets fired on the first cache-read only, which as you can imagine causes a lot of race conditions during transition
		// This is the recommended way to deal with complex scenarios involving cache + network reads
		// onCompleted will also fire incorrectly for concurrent calls on the same page, since it has an outdated copy of hasAlreadyFetchedInitialData telescoped in before some calls complete

		if (!skipQuery && isPreloadLoading) {
			// Mark this as preloading to prevent race conditions where loading is not yet set in caller
			setFooterCommentsLoadingState(FooterCommentsLoadingStateType.PRELOADING);
		}

		if (useStableCommentsDataUpdates && !isPreloadLoading && !skipQuery && data) {
			// Preload updates data, which we don't want
			updateCommentsDataState({
				data,
				readCommentsListState,
				updateData: hasAlreadyFetchedInitialData ? addNewCommentThreads : setCommentsDataMap,
				annotationsInEditorDoc,
				pageMode,
				currentUserId: currentUserId ?? '',
				isOpen: true,
			});

			// Don't update loading state if the query is skipped
			updateFooterCommentsLoadingState(data, setFooterCommentsLoadingState);
		}
	}, [
		data,
		useStableCommentsDataUpdates,
		hasAlreadyFetchedInitialData,
		addNewCommentThreads,
		setCommentsDataMap,
		annotationsInEditorDoc,
		pageMode,
		currentUserId,
		readCommentsListState,
		skipQuery,
		type,
		setFooterCommentsLoadingState,
		isPreloadLoading,
	]);

	return {
		loading: returnLoading, // If we skip the query make sure loading is false
		data,
		error,
		refetch,
	};
};
